import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import {
    rangeSelectItemPart,
    onItemPartSelectedOrChecked,
    itemPartSelected,
} from 'owa-mail-actions/lib/mailListSelectionActions';
import type React from 'react';
import { isMac } from 'owa-user-agent';
import { addToTimingMap } from 'owa-performance/lib/utils/timingMap';
import {
    isReadingPanePositionOff,
    isReadingPanePopoutOnly,
} from 'owa-mail-layout/lib/selectors/readingPanePosition';

/**
 * A handler for events created by user clicking mail list item part(s)
 * @param evt the mouse event
 * @param selectionSource the source of event
 * @param nodeId the given nodeId for the conversation item part
 * @param tableViewId - the table view id that this conversation belong to (or null if we don't care)
 * @returns a promise with boolean value based on if we have handled the event here
 */
export default function onMailListItemPartClickHandler(
    evt: React.MouseEvent<unknown> | KeyboardEvent,
    selectionSource: MailListItemSelectionSource,
    nodeId: string,
    tableViewId: string,
    itemId: string = '',
    allNodeIds: string[] = []
): void {
    evt.stopPropagation();

    if (isReadingPanePopoutOnly()) {
        // in popout only RP mode we should open RP on double click
        debounce(
            (simulateDoubleClick?: boolean) => {
                const selectionSourceToUse = simulateDoubleClick
                    ? MailListItemSelectionSource.MailListItemSimulatedDoubleClick
                    : selectionSource;
                onMailListItemPartClickHandlerInternal(
                    evt,
                    selectionSourceToUse,
                    nodeId,
                    tableViewId,
                    itemId,
                    allNodeIds
                );
            },
            selectionSource === MailListItemSelectionSource.MailListItemBodyDoubleClick,
            nodeId
        );
    } else {
        onMailListItemPartClickHandlerInternal(
            evt,
            selectionSource,
            nodeId,
            tableViewId,
            itemId,
            allNodeIds
        );
    }
}

function onMailListItemPartClickHandlerInternal(
    evt: React.MouseEvent<unknown> | KeyboardEvent,
    selectionSource: MailListItemSelectionSource,
    nodeId: string,
    tableViewId: string,
    itemId: string,
    allNodeIds: string[]
) {
    // If the OS is a Mac, use evt.metaKey to detect Cmd key
    const isCtrlOrCmdKeyDown = isMac() ? evt.metaKey : evt.ctrlKey;
    const tableView = getListViewStore().tableViews.get(tableViewId);

    if (!tableView) {
        return;
    }

    if (evt.shiftKey) {
        // If the user is holding down the shift key when they select an item part, then toggle select all rows between the anchor nodeId and the target nodeId
        rangeSelectItemPart(tableView, nodeId, isCtrlOrCmdKeyDown);
    } else if (
        isCtrlOrCmdKeyDown ||
        selectionSource === MailListItemSelectionSource.MailListItemCheckbox
    ) {
        // If the user is holding down the ctrl/cmd key or selecting an item part via the checkbox, toggle select the item part, keeping the previously selected item parts selected.
        onItemPartSelectedOrChecked(nodeId, tableView, isCtrlOrCmdKeyDown);
    } else {
        // If no cmd/ctrl or shift key pressed, single select the item part
        itemPartSelected(nodeId, itemId, allNodeIds, tableView, selectionSource);
    }
}

let timer: ReturnType<typeof setInterval>;
let pendingClickedNodeId: string = ''; // The nodeId of the clicked item waiting to be processed
const DEBOUNCE_DELAY = 200; // in ms

/**
 * Execute the callback and reset the pendingClickedRowKey (since the click is
 * no longer pending).
 *
 * @param callback the callback to execute
 * @param simulateDoubleClick if the user is double clicking
 */
const executeCallback = (callback: any, simulateDoubleClick: boolean = false) => {
    callback(simulateDoubleClick);
    pendingClickedNodeId = '';
    clearTimeout(timer);
};

/**
 * Debounce the callback if the user is clicking on the same row.
 *
 * @param callback the callback to execute
 * @param isDoubleClick if the user is double clicking
 * @param rowKey the rowKey of the clicked item
 */
const debounce = (callback: any, isDoubleClick: boolean, nodeId: string) => {
    // If there is a pending click, we need to check if the user is double clicking.
    if (pendingClickedNodeId) {
        clearTimeout(timer);

        // If the user is double clicking, we don't need to debounce.
        if (nodeId === pendingClickedNodeId) {
            executeCallback(callback, true /* simulateDoubleClick */);
            return;
        }
    }

    // If reading pane is on (or if an actual double click), we don't need
    // to debounce.
    if (isDoubleClick || !isReadingPanePositionOff()) {
        executeCallback(callback);
    } else {
        // Track the rowKey of the clicked item and debounce the callback.
        pendingClickedNodeId = nodeId;

        // If the user doesn't click again within DEBOUNCE_DELAY ms, we'll
        // process the click.
        timer = setTimeout(() => {
            addToTimingMap('setTimeout', 'onMailListItemClickHandler');
            executeCallback(callback);
        }, DEBOUNCE_DELAY);
    }
};
